import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { testIds } from "@/constants/testIds";
import { toast } from "sonner";
import { Plus, Save, Trash2, Percent, Banknote } from "lucide-react";

export default function SettingsView() {
  const [tiers, setTiers] = useState([]);
  const [commission, setCommission] = useState(15);
  const [saving, setSaving] = useState(false);
  const [savingComm, setSavingComm] = useState(false);

  useEffect(() => {
    api.get("/discount-tiers").then((r) => setTiers(r.data.tiers || []));
    api
      .get("/commission")
      .then((r) => setCommission(r.data.percent))
      .catch(() => {});
  }, []);

  const updateTier = (i, key, val) =>
    setTiers((prev) =>
      prev.map((t, idx) => (idx === i ? { ...t, [key]: Number(val) } : t)),
    );
  const removeTier = (i) =>
    setTiers((prev) => prev.filter((_, idx) => idx !== i));
  const addTier = () =>
    setTiers((prev) => [...prev, { min_days: 5, percent: 15 }]);

  const saveDiscounts = async () => {
    setSaving(true);
    try {
      const payload = {
        tiers: tiers.map((t) => ({
          min_days: Number(t.min_days),
          percent: Number(t.percent),
        })),
      };
      const { data } = await api.put("/discount-tiers", payload);
      setTiers(data.tiers);
      toast.success("Sconti aggiornati");
    } catch {
      toast.error("Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const saveCommission = async () => {
    setSavingComm(true);
    try {
      const { data } = await api.put("/commission", {
        percent: Number(commission),
      });
      setCommission(data.percent);
      toast.success("Commissione aggiornata");
    } catch {
      toast.error("Errore salvataggio commissione");
    } finally {
      setSavingComm(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Commission section */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Banknote size={16} className="text-emerald-400" />
          <h2 className="font-heading text-lg sm:text-xl font-black uppercase tracking-tight text-white">
            Commissione partner
          </h2>
        </div>
        <p className="text-xs text-zinc-500 mb-4">
          Percentuale che trattieni sul materiale dei collaboratori. Appare nel PDF interno di ogni preventivo (visibile solo a te) e nelle statistiche di incasso mensili.
        </p>
        <div className="bg-zinc-900/90 border border-zinc-800 rounded-xl p-4 flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[140px]">
            <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
              % sul materiale partner
            </label>
            <div className="relative">
              <input
                data-testid={testIds.adminCommissionInput}
                type="number"
                min={0}
                max={100}
                step={0.5}
                value={commission}
                onChange={(e) => setCommission(e.target.value)}
                className="w-full min-h-[44px] pl-3 pr-8 rounded-lg bg-zinc-950 border border-zinc-800 focus:border-emerald-600/60 focus:outline-none text-white text-base font-mono font-bold"
              />
              <Percent
                size={14}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600"
              />
            </div>
          </div>
          <button
            data-testid={testIds.adminCommissionSave}
            onClick={saveCommission}
            disabled={savingComm}
            className="min-h-[44px] px-4 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-heading font-black uppercase tracking-wider text-xs shadow-lg shadow-emerald-900/40 transition flex items-center gap-2"
          >
            <Save size={14} /> {savingComm ? "Salvo..." : "Salva"}
          </button>
        </div>
      </div>

      {/* Discount tiers section */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Percent size={16} className="text-rose-500" />
          <h2 className="font-heading text-lg sm:text-xl font-black uppercase tracking-tight text-white">
            Sconti multi-giorno
          </h2>
        </div>
        <p className="text-xs text-zinc-500 mb-4">
          Applicati automaticamente al totale in base ai giorni di noleggio. Quando più soglie sono valide, viene usata quella più alta.
        </p>

        <div className="space-y-2">
          {tiers.length === 0 && (
            <div className="text-center py-6 text-zinc-500 text-xs font-mono uppercase tracking-widest border border-dashed border-zinc-800 rounded-xl">
              Nessuno sconto configurato
            </div>
          )}
          {tiers.map((t, i) => (
            <div
              key={i}
              className="bg-zinc-900/90 border border-zinc-800 rounded-xl p-3 flex items-center gap-2"
            >
              <div className="flex-1 grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                    Da (giorni)
                  </label>
                  <input
                    data-testid={testIds.adminTierMinDays(i)}
                    type="number"
                    min={1}
                    value={t.min_days}
                    onChange={(e) => updateTier(i, "min_days", e.target.value)}
                    className="w-full min-h-[42px] px-3 rounded-lg bg-zinc-950 border border-zinc-800 focus:border-rose-600/60 focus:outline-none text-white text-sm font-mono"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                    Sconto %
                  </label>
                  <div className="relative">
                    <input
                      data-testid={testIds.adminTierPercent(i)}
                      type="number"
                      min={0}
                      max={100}
                      step={0.5}
                      value={t.percent}
                      onChange={(e) => updateTier(i, "percent", e.target.value)}
                      className="w-full min-h-[42px] pl-3 pr-8 rounded-lg bg-zinc-950 border border-zinc-800 focus:border-rose-600/60 focus:outline-none text-white text-sm font-mono"
                    />
                    <Percent
                      size={12}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-600"
                    />
                  </div>
                </div>
              </div>
              <button
                data-testid={testIds.adminTierRemove(i)}
                onClick={() => removeTier(i)}
                className="w-10 h-10 rounded-lg bg-zinc-800 hover:bg-rose-950 text-zinc-400 hover:text-rose-400 flex items-center justify-center transition self-end"
                aria-label="Rimuovi soglia"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mt-3">
          <button
            data-testid={testIds.adminTierAdd}
            onClick={addTier}
            className="min-h-[44px] px-4 rounded-lg bg-zinc-900 border border-zinc-700 hover:border-zinc-600 text-zinc-200 text-xs font-semibold uppercase tracking-wider transition flex items-center gap-2"
          >
            <Plus size={14} /> Aggiungi soglia
          </button>
          <button
            data-testid={testIds.adminTiersSave}
            onClick={saveDiscounts}
            disabled={saving}
            className="min-h-[44px] px-4 rounded-lg bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white font-heading font-black uppercase tracking-wider text-xs shadow-lg shadow-rose-900/40 transition flex items-center gap-2 ml-auto"
          >
            <Save size={14} /> {saving ? "Salvo..." : "Salva sconti"}
          </button>
        </div>
      </div>
    </div>
  );
}
